import { DatabaseRunMetrics } from 'libs/end-to-end/src/database-run-metrics';
import React, { useMemo } from 'react';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveSankey } from '@nivo/sankey';
import { LoadingSkeleton } from 'apps/sigma2-ui/src/components/LoadingSkeleton';

export const DatabaseRunMetricsPage = ({
    runId,
    organizationId,
    databaseId,
    scheduleId,
}: {
    runId: string;
    organizationId: string;
    databaseId: string;
    scheduleId: string;
}) => {

    const readLatestRunMetricsPattern = useSimpleReadPattern(
        useMemo(() => {
            return {
                getUrlFn: () => {
                    return `${getApiBaseUrl()}/v2/organizations/${organizationId}/databases/${databaseId}/schedule/${scheduleId}/runs/metrics/${runId}`;
                },
                canRead: true,
                responseSchema: DatabaseRunMetrics,
            };
        }, [runId, organizationId, databaseId, scheduleId]));

    const metrics = useMemo(() => {
        if (readLatestRunMetricsPattern.status !== 'done') {
            return null;
        }
        return readLatestRunMetricsPattern.entity;
    }, [readLatestRunMetricsPattern]);

    // Transform the distributionOfHiringScore data for Nivo line chart
    const chartData = useMemo(() => {
        if (!metrics?.distributionOfHiringScore) {
            return [];
        }

        // Sort by hiring score to ensure proper line progression
        const sortedData = [...metrics.distributionOfHiringScore].sort((a, b) => a.hiringScore - b.hiringScore);

        return [
            {
                id: 'Candidates by Hiring Score',
                data: sortedData.map(item => ({
                    x: item.hiringScore,
                    y: item.numberOfCandidates,
                })),
            },
        ];
    }, [metrics?.distributionOfHiringScore]);

    // Transform metrics data for Nivo Sankey chart
    const sankeyData = useMemo(() => {
        if (!metrics) return { nodes: [], links: [] };

        const nodes = [
            // Source nodes
            { id: 'Total Candidates', nodeColor: '#1f77b4' },
            { id: 'With Hiring Score', nodeColor: '#ff7f0e' },
            { id: 'Writebacks Attempted', nodeColor: '#2ca02c' },
            
            // Hiring Writeback Outcome nodes
            { id: 'Writebacks Succeeded', nodeColor: '#d62728' },
            { id: 'Writebacks Failed', nodeColor: '#9467bd' },
            { id: 'Writebacks Skipped', nodeColor: '#8c564b' },

            // Stage Change Writeback nodes
            { id: 'Stage Change Writebacks Attempted', nodeColor: '#3465a4' },
            { id: 'Stage Change Writebacks Succeeded', nodeColor: '#d62728' },
            { id: 'Stage Change Writebacks Failed', nodeColor: '#9467bd' },
            { id: 'Stage Change Writebacks Skipped', nodeColor: '#8c564b' },
        ];

        const links = [
            // Main flow
            {
                source: 'Total Candidates',
                target: 'With Hiring Score',
                value: metrics.numberOfCandidatesWithHiringScore,
                linkColor: '#1f77b4',
            },
            {
                source: 'With Hiring Score',
                target: 'Writebacks Attempted',
                value: metrics.hiringScoreWritebackMetrics.totalAttempted || 0,
                linkColor: '#ff7f0e',
            },
            // Outcome distribution
            {
                source: 'Writebacks Attempted',
                target: 'Writebacks Succeeded',
                value: metrics.hiringScoreWritebackMetrics.succeeded || 0,
                linkColor: '#2ca02c',
            },
            {
                source: 'Writebacks Attempted',
                target: 'Writebacks Failed',
                value: metrics.hiringScoreWritebackMetrics.failed || 0,
                linkColor: '#2ca02c',
            },
            {
                source: 'Writebacks Attempted',
                target: 'Writebacks Skipped',
                value: metrics.hiringScoreWritebackMetrics.skipped || 0,
                linkColor: '#2ca02c',
            },

            // Stage Change Writebacks (flow from hiring writeback outcomes)
            {
                source: 'Writebacks Succeeded',
                target: 'Stage Change Writebacks Attempted',
                value: Math.floor((metrics.stageChangeWritebackMetrics.totalAttempted || 0)), // 70% from successful
                linkColor: '#3465a4',
            },
            {
                source: 'Writebacks Skipped',
                target: 'Stage Change Writebacks Attempted',
                value: Math.floor((metrics.stageChangeWritebackMetrics.totalAttempted || 0)), // 30% from skipped
                linkColor: '#3465a4',
            },
            {
                source: 'Writebacks Failed',
                target: 'Stage Change Writebacks Attempted',
                value: 0, // 30% from skipped
                linkColor: '#3465a4',
            },
            {
                source: 'Stage Change Writebacks Attempted',
                target: 'Stage Change Writebacks Succeeded',
                value: metrics.stageChangeWritebackMetrics.succeeded || 0,
                linkColor: '#d62728',
            },
            {
                source: 'Stage Change Writebacks Attempted',
                target: 'Stage Change Writebacks Failed',
                value: metrics.stageChangeWritebackMetrics.failed || 0,
                linkColor: '#9467bd',
            },
            {
                source: 'Stage Change Writebacks Attempted',
                target: 'Stage Change Writebacks Skipped',
                value: metrics.stageChangeWritebackMetrics.skipped || 0,
                linkColor: '#8c564b',
            },
        ]

        return { nodes, links };
    }, [metrics]);

  return <Spacing size='sm' direction='vertical'>
    {readLatestRunMetricsPattern.status === 'loading' && (
        <LoadingSkeleton heightClassName='h-10' />
    )}
    {metrics && (
        <>
        <Spacing size='sm' direction='vertical'>
            <S2Typography
                variant={'headline-7-alt'}
                translatedContent={`${metrics.numberOfCandidatesInRevisionIndex} total candidates`}
            />
            <S2Typography
                variant={'headline-7-alt'}
                translatedContent={`${metrics.numberOfCandidatesWithHiringScore} candidates with hiring score`}
            />
            <S2Typography
                variant={'headline-7-alt'}
                translatedContent={`${metrics.hiringScoreWritebackMetrics.totalAttempted} hiring writebacks attempted`}
            />
            <S2Typography
                variant={'headline-7-alt'}
                translatedContent={`${metrics.hiringScoreWritebackMetrics.succeeded} hiring writebacks succeeded`}
            />
            <S2Typography
                variant={'headline-7-alt'}
                translatedContent={`${metrics.hiringScoreWritebackMetrics.failed} hiring writebacks failed`}
            />
            <S2Typography
                variant={'headline-7-alt'}
                translatedContent={`${metrics.hiringScoreWritebackMetrics.skipped} hiring writebacks skipped`}
            />
            <S2Typography
                variant={'headline-7-alt'}
                translatedContent={`${metrics.stageChangeWritebackMetrics.totalAttempted} stage change writebacks attempted`}
            />
            <S2Typography
                variant={'headline-7-alt'}
                translatedContent={`${metrics.stageChangeWritebackMetrics.succeeded} stage change writebacks succeeded`}
            />
            <S2Typography
                variant={'headline-7-alt'}
                translatedContent={`${metrics.stageChangeWritebackMetrics.failed} stage change writebacks failed`}
            />
            <S2Typography
                variant={'headline-7-alt'}
                translatedContent={`${metrics.stageChangeWritebackMetrics.skipped} stage change writebacks skipped`}
            />
            </Spacing>
            
            {/* Hiring Score Distribution Chart */}
            {chartData.length > 0 && (
                <Spacing size='md' direction='vertical'>
                    <S2Typography
                        variant={'headline-6-alt'}
                        translatedContent={'Hiring Score Distribution'}
                    />
                    <div style={{ height: '400px', width: '100%' }}>
                        <ResponsiveLine
                            data={chartData}
                            margin={{ top: 20, right: 20, bottom: 60, left: 60 }}
                            xScale={{ type: 'linear', min: 'auto', max: 'auto' }}
                            yScale={{ type: 'linear', min: 0, max: 'auto' }}
                            axisTop={null}
                            axisRight={null}
                            axisBottom={{
                                tickSize: 5,
                                tickPadding: 5,
                                tickRotation: 0,
                                legend: 'Hiring Score',
                                legendOffset: 40,
                                legendPosition: 'middle',
                            }}
                            axisLeft={{
                                tickSize: 5,
                                tickPadding: 5,
                                tickRotation: 0,
                                legend: 'Number of Candidates',
                                legendOffset: -50,
                                legendPosition: 'middle',
                            }}
                            pointSize={8}
                            pointColor={{ theme: 'background' }}
                            pointBorderWidth={2}
                            pointBorderColor={{ from: 'serieColor' }}
                            pointLabelYOffset={-12}
                            useMesh={true}
                            legends={[
                                {
                                    anchor: 'top',
                                    direction: 'row',
                                    justify: false,
                                    translateX: 0,
                                    translateY: -20,
                                    itemsSpacing: 0,
                                    itemDirection: 'left-to-right',
                                    itemWidth: 80,
                                    itemHeight: 20,
                                    itemOpacity: 0.75,
                                    symbolSize: 12,
                                    symbolShape: 'circle',
                                    symbolBorderColor: 'rgba(0, 0, 0, .5)',
                                    effects: [
                                        {
                                            on: 'hover',
                                            style: {
                                                itemBackground: 'rgba(0, 0, 0, .03)',
                                                itemOpacity: 1,
                                            },
                                        },
                                    ],
                                },
                            ]}
                            theme={{
                                axis: {
                                    domain: {
                                        line: {
                                            stroke: '#777777',
                                            strokeWidth: 1,
                                        },
                                    },
                                    ticks: {
                                        line: {
                                            stroke: '#777777',
                                            strokeWidth: 1,
                                        },
                                        text: {
                                            fill: '#333333',
                                            fontSize: 12,
                                        },
                                    },
                                    legend: {
                                        text: {
                                            fill: '#333333',
                                            fontSize: 14,
                                            fontWeight: 'bold',
                                        },
                                    },
                                },
                                grid: {
                                    line: {
                                        stroke: '#dddddd',
                                        strokeWidth: 1,
                                    },
                                },
                                tooltip: {
                                    container: {
                                        background: '#ffffff',
                                        color: '#333333',
                                        fontSize: 12,
                                        borderRadius: 4,
                                        boxShadow: '0 3px 6px rgba(0,0,0,0.2)',
                                    },
                                },
                            }}
                        />
                    </div>
                </Spacing>
            )}

            {/* Hiring Process Flow Chart */}
            {sankeyData.links.length > 0 && (
                <Spacing size='md' direction='vertical'>
                    <S2Typography
                        variant={'headline-6-alt'}
                        translatedContent={'Hiring Process Flow'}
                    />
                    <div style={{ height: '500px', width: '100%' }}>
                        <ResponsiveSankey
                            data={sankeyData}
                            margin={{ top: 50, right: 50, bottom: 50, left: 50 }}
                            align="justify"
                            colors={{ scheme: 'category10' }}
                            nodeOpacity={1}
                            nodeHoverOthersOpacity={0.35}
                            nodeThickness={18}
                            nodeSpacing={24}
                            nodeBorderWidth={0}
                            nodeBorderRadius={3}
                            linkOpacity={0.5}
                            linkHoverOthersOpacity={0.1}
                            linkContract={3}
                            enableLinkGradient={true}
                            labelPosition="inside"
                            labelOrientation="horizontal"
                            labelPadding={0}
                            labelTextColor={{ from: 'color', modifiers: [['darker', 1]] }}
                            animate={true}
                            theme={{
                                tooltip: {
                                    container: {
                                        background: '#ffffff',
                                        color: '#333333',
                                        fontSize: 12,
                                        borderRadius: 4,
                                        boxShadow: '0 3px 6px rgba(0,0,0,0.2)',
                                    },
                                },
                            }}
                        />
                    </div>
                </Spacing>
            )}
        </>
    )}
  </Spacing>;
};
